import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import Skeleton from "@/components/ui/Skeleton";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import VerdictBadge from "@/components/VerdictBadge";
import { ShieldAlert, Microscope, Home, CheckCircle2, Share2 } from "lucide-react";
import DealTypePill from "@/components/deal/DealTypePill";
import RolePill from "@/components/deal/RolePill";
import FlipFinancialsTab, { ExitAsRentalPanel } from "@/components/deal/FlipFinancialsTab";
import {
  CalculationTrace,
  CalculationTraceToggle,
  useCalculationTrace,
} from "@/components/deal/CalculationTrace";
import BRRRRFinancialsTab from "@/components/deal/BRRRRFinancialsTab";
import EffectivePriceBanner from "@/components/deal/EffectivePriceBanner";
import { effectivePriceBasisSuffix } from "@/lib/effectivePrice";
import MultifamilySummaryCard from "@/components/deal/MultifamilySummaryCard";
import ScenariosTab from "@/components/deal/ScenariosTab";
import CriteriaTab from "@/components/deal/CriteriaTab";
import StaleAnalysisBanner from "@/components/deal/StaleAnalysisBanner";
import ScenarioComparison from "@/components/deal/ScenarioComparison";
import PerformanceTab from "@/components/deal/PerformanceTab";
import FXAdjustedReturnsPanel from "@/components/deal/FXAdjustedReturnsPanel";
import HistoricalFXChart from "@/components/deal/HistoricalFXChart";
import CompareStrategiesModal from "@/components/deal/CompareStrategiesModal";
import CompareToModal from "@/components/compare/CompareToModal";
import MarkAsBoughtModal from "@/components/deal/MarkAsBoughtModal";
import ShareCardModal from "@/components/deal/ShareCardModal";
import StagePicker from "@/components/pipeline/StagePicker";
import ContactCard from "@/components/deal/ContactCard";
import RealizedHero from "@/components/deal/RealizedHero";
import AiHeroPanel from "@/components/deal/AiHeroPanel";
import CompsTab from "@/components/deal/CompsTab";
import DealScoreTriView from "@/components/deal/DealScoreTriView";
import PersonalResidenceScenarios from "@/components/deal/PersonalResidenceScenarios";
import PersonalResidenceTriView from "@/components/deal/PersonalResidenceTriView";
import ScoreBreakdownPanel from "@/components/deal/ScoreBreakdownPanel";
import AnalysisBasisChip from "@/components/deal/AnalysisBasisChip";
import AiAnalysisTab from "@/components/deal/AiAnalysisTab";
import RiskTab from "@/components/deal/RiskTab";
import NegotiationTab from "@/components/deal/NegotiationTab";
import DocumentsTab from "@/components/deal/DocumentsTab";
import DocumentHealthCard from "@/components/deal/DocumentHealthCard";
import ProjectionTab from "@/components/deal/ProjectionTab";
import SyndicationTab from "@/components/deal/SyndicationTab";
import TabErrorBoundary from "@/components/TabErrorBoundary";
import FxRiskBanner from "@/components/deal/FxRiskBanner";
import TaxImpactPanel from "@/components/deal/TaxImpactPanel";
import ProjectionModal from "@/components/deal/ProjectionModal";
import MarketSnapshotCard from "@/components/market/MarketSnapshotCard";
import Reports from "@/pages/Reports";
import api from "@/lib/api";
import {
  formatDscr,
  formatScore,
  propertyTypeLabel,
} from "@/lib/format";
import { useFmt } from "@/lib/useFmt";
import { PropertyDetailLine, PropertyDescription } from "@/components/property/PropertyMeta";
import {
  ArrowLeft,
  Building2,
  Edit3,
  Trash2,
  Loader2,
  Sparkles,
  Lock,
  TrendingUp,
} from "lucide-react";

function KpiTile({ label, value, sub, tone = "default", testid, trace, traceMetric, traceEnabled }) {
  const toneCls =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50/50"
      : tone === "bad"
      ? "border-red-200 bg-red-50/50"
      : "border-slate-200 bg-white";
  // Phase 45 — Calculation trace wrapper. When enabled and a
  // matching trace[traceMetric] exists, the value renders with a
  // dotted under-line and shows the trace block on hover.
  const valueNode = (
    <div className="mt-2 text-2xl font-bold tracking-tight text-slate-900">{value}</div>
  );
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border p-5 shadow-sm transition-all hover:shadow-md ${toneCls}`}
    >
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
        {label}
      </div>
      {trace && traceMetric ? (
        <CalculationTrace trace={trace} metric={traceMetric} enabled={traceEnabled}>
          {valueNode}
        </CalculationTrace>
      ) : (
        valueNode
      )}
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function ComingSoonTab({ title, description, phase = "Phase 3" }) {
  return (
    <div
      data-testid="coming-soon"
      className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white p-16 text-center"
    >
      <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
        <Sparkles className="h-6 w-6" />
      </div>
      <h3 className="text-xl font-semibold text-slate-900">{title}</h3>
      <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">{description}</p>
      <span className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-slate-600">
        <Lock className="h-3 w-3" /> Available in {phase}
      </span>
    </div>
  );
}

function FinancialsRow({ label, value, indent = false, bold = false, divider = false, negative = false }) {
  return (
    <tr className={`${divider ? "border-t border-slate-200" : ""}`}>
      <td className={`py-2.5 pr-4 text-sm ${indent ? "pl-8 text-slate-500" : "pl-0 text-slate-700"} ${bold ? "font-semibold text-slate-900" : ""}`}>
        {label}
      </td>
      <td className={`py-2.5 pl-4 text-right text-sm tabular-nums ${bold ? "font-bold text-slate-900" : ""} ${negative ? "text-red-600" : "text-slate-900"}`}>
        {value}
      </td>
    </tr>
  );
}

export default function DealDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fmt = useFmt();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [aiRunning, setAiRunning] = useState({ analyze: false, risks: false, neg: false });
  const [projOpen, setProjOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareToOpen, setCompareToOpen] = useState(false);
  const [markBoughtOpen, setMarkBoughtOpen] = useState(false);
  const [shareCardOpen, setShareCardOpen] = useState(false);
  const [riskConfirmOpen, setRiskConfirmOpen] = useState(false);
  // Phase 36 Batch AB — controlled Tabs so child components can
  // navigate (e.g. NegotiationTab's "View documents →" deep link).
  const [activeTab, setActiveTab] = useState("summary");
  // Phase 36 Batch Y — currency risk for the deal's property_currency.
  // Fetched on demand once the deal is loaded and fx_view.fx_applicable.
  const [currencyRisk, setCurrencyRisk] = useState(null);
  // Phase 45 — Calculation trace toggle. Persisted to localStorage so
  // an analyst who likes the trace mode keeps it across sessions.
  const { enabled: traceEnabled, setEnabled: setTraceEnabled } = useCalculationTrace(
    typeof window !== "undefined" && window.localStorage?.getItem?.("runidea_calc_trace") === "1"
  );
  const onToggleTrace = (v) => {
    setTraceEnabled(v);
    if (typeof window !== "undefined") {
      window.localStorage?.setItem?.("runidea_calc_trace", v ? "1" : "0");
    }
  };

  const runAi = async (kind) => {
    const endpoint = kind === "analyze" ? "analyze" : kind === "risks" ? "risks" : "negotiation";
    setAiRunning((s) => ({ ...s, [kind]: true }));
    try {
      const { data } = await api.post(`/deals/${id}/${endpoint}`);
      setDeal(data);
      const ok =
        kind === "analyze"
          ? data?.ai_summary?.ai_status
          : kind === "risks"
          ? data?.risk_analysis?.ai_status
          : data?.negotiation?.ai_status;
      if (ok === "fallback") {
        toast("AI service unavailable — showing baseline analysis.");
      } else {
        toast.success(`${kind === "analyze" ? "AI analysis" : kind === "risks" ? "Risk analysis" : "Negotiation analysis"} updated.`);
      }
    } catch (e) {
      toast.error("AI call failed — please try again.");
    } finally {
      setAiRunning((s) => ({ ...s, [kind]: false }));
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/deals/${id}`);
        setDeal(data);
      } catch (e) {
        if (e?.response?.status === 404) {
          toast.error("Deal not found.");
        } else {
          toast.error("Could not load deal.");
        }
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  // Phase 36 Batch Y — Currency risk fetch. Runs once the deal is in
  // state and we've confirmed fx_view.fx_applicable. Skip entirely when
  // same-currency. The endpoint is 24h-cached server-side so refreshing
  // the page is cheap.
  useEffect(() => {
    const view = deal?.fx_view;
    if (!view?.fx_applicable) {
      setCurrencyRisk(null);
      return undefined;
    }
    const pc = view.property_currency;
    const bc = view.base_currency || "USD";
    if (!pc) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/fx/risk", {
          params: { currency: pc, user_base: bc },
        });
        if (!cancelled) setCurrencyRisk(data);
      } catch {
        if (!cancelled) setCurrencyRisk(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [deal?.fx_view]);

  const onDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/deals/${id}`);
      toast.success("Deal deleted.");
      navigate("/dashboard");
    } catch {
      toast.error("Failed to delete.");
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="space-y-8" data-testid="dealdetail-loading">
          {/* Hero block placeholder */}
          <div className="space-y-3">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-10 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
          </div>
          {/* Toolbar placeholder */}
          <div className="flex flex-wrap gap-2">
            {[0,1,2,3,4].map((i) => <Skeleton key={i} className="h-9 w-32" />)}
          </div>
          {/* Tab strip + content placeholder */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-6 flex gap-4 border-b border-slate-200 pb-3">
              {[0,1,2,3].map((i) => <Skeleton key={i} className="h-3 w-20" />)}
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Skeleton.Card />
              <Skeleton.Card />
              <Skeleton.Card />
              <Skeleton.Card />
            </div>
          </div>
        </div>
      </AppShell>
    );
  }
  if (!deal) return null;

  const p = deal.property || {};
  const cashFlowTone = deal.cash_flow_monthly >= 0 ? "good" : "bad";

  return (
    <AppShell>
      <div className="space-y-8" data-testid="deal-detail-root">
        {/* Header */}
        <div>
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            data-testid="deal-back-button"
            className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 transition-colors hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4" /> All deals
          </button>

          <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl" data-testid="deal-address">
                  {p.address || "—"}
                </h1>
                <DealTypePill type={deal.deal_type} size="lg" testid="deal-type-pill" />
                <RolePill deal={deal} onChange={setDeal} />
                {/* Phase 36 Batch P — payback chip. Reads role-aware
                    payback from metrics_at_role (already on the deal
                    response). "Never" when CF ≤ 0 ('—' style).

                    Phase 44 Batch BF2 — flip deals don't have rental
                    payback (cash_flow_monthly is None). Render the
                    HOLD chip instead, anchored on holding_period_months. */}
                {deal.deal_type === "flip" ? (
                  <span
                    data-testid="deal-hold-chip"
                    className="inline-flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-orange-700"
                    title="Months from close to resale on this flip"
                  >
                    <span className="text-orange-500">Hold:</span>
                    {deal.holding_period_months || 6} months
                  </span>
                ) : (() => {
                  const pp = deal?.metrics_at_role?.payback_period_years;
                  return (
                    <span
                      data-testid="deal-payback-chip"
                      className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-600"
                      title="Years to recoup cash invested at current cash flow"
                    >
                      <span className="text-slate-400">Payback:</span>
                      {pp == null ? "Never" : `${Number(pp).toFixed(1)} yrs`}
                    </span>
                  );
                })()}
              </div>
              <p className="mt-2 flex items-center gap-1.5 text-sm text-slate-500" data-testid="deal-detail-zip">
                {/* Phase 36 Batch R — country flag + region name in the
                    detail page header. Reads region_code with legacy
                    fallback to `state`, country_iso2 with fallback to
                    `country`. */}
                {(() => {
                  // Compute the region's full display name from the
                  // deal's metrics_at_role currency context isn't on
                  // hand here, so we render: 🇺🇸 City, FL · ZIP.
                  const iso2 = (p.country_iso2 || p.country || "US").toUpperCase();
                  const A = 0x1F1E6;
                  const flag = iso2.length === 2
                    ? String.fromCodePoint(
                        A + iso2.charCodeAt(0) - 65,
                        A + iso2.charCodeAt(1) - 65,
                      )
                    : "";
                  const region = p.region_code || p.state || "";
                  const bits = [p.city, region].filter(Boolean).join(", ");
                  const tail = p.postal_code || p.zip || "";
                  return (
                    <>
                      <span data-testid="deal-detail-flag" className="text-base">{flag}</span>
                      <span>{[bits, tail].filter(Boolean).join(" · ")}</span>
                    </>
                  );
                })()}
              </p>
              {/* Shared deal-row meta — keeps DealDetail in lock-step with
                  Portfolio / Dashboard / Pipeline / Compare formatting. */}
              <PropertyDetailLine d={deal} fmt={fmt} className="mt-1" />
              <PropertyDescription d={deal} fmt={fmt} />
              {/* Phase 44 Batch BF2 — flip header subtitle.
                  Replaces the rental "monthly cash flow" hero phrase with
                  the flip's defining metrics: hold, ARV target, repairs.
                  Only renders on `deal_type === "flip"`. */}
              {deal.deal_type === "flip" ? (
                <div
                  data-testid="deal-flip-header-strip"
                  className="mt-3 flex flex-wrap items-center gap-2 text-sm text-slate-700"
                >
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-orange-700 ring-1 ring-orange-200"
                        data-testid="deal-flip-hold">
                    HOLD: {deal.holding_period_months || 0} months
                  </span>
                  <span className="text-slate-500" data-testid="deal-flip-subtitle">
                    Flip targeting{" "}
                    <span className="font-semibold text-slate-900 tabular-nums">{fmt.money(deal.arv)}</span>
                    {" ARV with "}
                    <span className="font-semibold text-slate-900 tabular-nums">{fmt.money(deal.repair_budget)}</span>
                    {" repair budget"}
                  </span>
                </div>
              ) : null}
            </div>

            <div className="flex items-center gap-3">
              {/* Phase 45 — Calculation-trace toggle. Persisted to
                  localStorage so analysts who prefer the trace mode
                  keep it across sessions. */}
              <CalculationTraceToggle enabled={traceEnabled} onToggle={onToggleTrace} />
              <div
                data-testid="deal-score-card"
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-2 shadow-sm"
              >
                <div className="text-right">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Deal score</div>
                  <div className="text-2xl font-bold tabular-nums text-slate-900">
                    {formatScore(deal.deal_score)}
                    <span className="ml-0.5 text-base font-medium text-slate-400">/ 100</span>
                  </div>
                </div>
                <VerdictBadge verdict={deal.verdict} size="lg" />
              </div>
            </div>
          </div>

          {deal.admin_override ? (
            <div
              data-testid="admin-override-notice"
              className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            >
              <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-700" />
              <div>
                <p className="font-semibold">Admin override: <span className="rounded bg-white/60 px-1.5 py-0.5 font-mono text-[11px] uppercase">{deal.admin_override.verdict}</span></p>
                <p className="mt-0.5 text-amber-800">{deal.admin_override.reason}</p>
                {deal.admin_override.by_admin_email ? (
                  <p className="mt-1 text-[11px] text-amber-700">By {deal.admin_override.by_admin_email}</p>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* Batch E1 — Pipeline stage picker. Sits between the header
              and the primary action buttons so it's instantly reachable
              when the user lands on the deal.
              Phase 36 — pair it with the Contact card in a 2-column grid
              so the realtor/FSBO + click-to-call info is always at the
              top of the deal page. Stacks on mobile. */}
          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="deal-top-cards">
            <div data-testid="deal-stage-block">
              <StagePicker
                stage={deal.pipeline_stage || "prospect"}
                dealId={deal.id}
                onChange={(s) =>
                  setDeal((prev) => (prev ? { ...prev, pipeline_stage: s } : prev))
                }
              />
            </div>
            <ContactCard deal={deal} />
          </div>

          {/* Phase 44 Batch BF2 — Flip header strip. Renders the
              hold-period + ARV target + repair budget as a single
              line of context directly under the action bar, so the
              user immediately sees "HOLD: 6 months · Flip targeting
              $300k ARV with $90k repair budget". The AVM warning
              chip sits inline when the discrepancy fires. */}
          {deal.deal_type === "flip" ? (
            <div
              data-testid="flip-header-strip"
              className="mt-5 flex flex-wrap items-center gap-3 rounded-xl border border-orange-200 bg-orange-50/60 px-4 py-3"
            >
              <span
                data-testid="flip-header-hold"
                className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wider text-orange-800 ring-1 ring-orange-200"
              >
                HOLD: {deal.holding_period_months ?? "?"} months
              </span>
              <span data-testid="flip-header-subtitle" className="text-sm text-slate-700">
                Flip targeting{" "}
                <span className="font-semibold tabular-nums text-slate-900">
                  {fmt.money(deal.arv)}
                </span>{" "}
                ARV with{" "}
                <span className="font-semibold tabular-nums text-slate-900">
                  {fmt.money(deal.repair_budget)}
                </span>{" "}
                repair budget
              </span>
              {deal.avm_discrepancy_warning ? (
                <span
                  data-testid="flip-header-avm-chip"
                  data-severity={deal.avm_discrepancy_severity || "medium"}
                  className={`ml-auto inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider ring-1 ${
                    deal.avm_discrepancy_severity === "high"
                      ? "bg-rose-50 text-rose-800 ring-rose-200"
                      : "bg-amber-50 text-amber-800 ring-amber-200"
                  }`}
                  title="Asking price is far below AVM — verify the listing"
                >
                  <ShieldAlert className="h-3.5 w-3.5" />
                  AVM {((deal.asking_vs_avm_pct_below || 0) * 100).toFixed(0)}% gap
                </span>
              ) : null}
            </div>
          ) : null}

          {/* Batch F — Realized hero. Renders only for owned deals
              (realized_metrics is attached by the backend when
              is_owned=true). Sits between the top cards and the tabs
              so it owns the page hero on owned properties. */}
          {deal.is_owned ? <div className="mt-5"><RealizedHero deal={deal} /></div> : null}

          <div className="mt-5 flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={() => navigate(`/deals/${id}/edit`)}
              data-testid="deal-edit-button"
              className="border-slate-200 text-slate-700 hover:bg-slate-50"
            >
              <Edit3 className="mr-2 h-4 w-4" /> Edit Inputs
            </Button>
            <Button
              variant="outline"
              onClick={() => setCompareOpen(true)}
              data-testid="compare-strategies-trigger"
              className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            >
              <Microscope className="mr-2 h-4 w-4" /> Compare All Strategies
            </Button>
            <Button
              variant="outline"
              onClick={() => setCompareToOpen(true)}
              data-testid="compare-to-trigger"
              className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            >
              <Microscope className="mr-2 h-4 w-4" /> Compare to…
            </Button>
            {/* Phase 31 — Mark as bought / Owned badge */}
            {deal.is_owned ? (
              <>
                <span
                  className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1.5 text-sm font-bold text-emerald-800"
                  data-testid="deal-owned-badge"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Owned since {deal.purchase_year || "—"}
                </span>
                {/* Phase 32 — Share milestone */}
                <Button
                  onClick={() => setShareCardOpen(true)}
                  data-testid="share-milestone-trigger"
                  variant="outline"
                  className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                >
                  <Share2 className="mr-2 h-4 w-4" />
                  Share milestone
                </Button>
              </>
            ) : (deal.verdict || "").toLowerCase() === "reject" ? null : (
              <Button
                onClick={() => {
                  if ((deal.verdict || "").toLowerCase() === "risky") {
                    setRiskConfirmOpen(true);
                  } else {
                    setMarkBoughtOpen(true);
                  }
                }}
                data-testid="mark-as-bought-trigger"
                className="bg-emerald-600 text-white hover:bg-emerald-700"
              >
                <Home className="mr-2 h-4 w-4" />
                Mark as bought
                {(deal.verdict || "").toLowerCase() === "risky" && (
                  <span className="ms-1 text-amber-200">(?)</span>
                )}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => navigate(`/manage/properties/${deal.property_id}`)}
              data-testid="deal-manage-property-button"
              className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            >
              <Building2 className="mr-2 h-4 w-4" /> Manage Property
            </Button>
            <Button
              variant="outline"
              onClick={() => setProjOpen(true)}
              data-testid="deal-projection-button"
              className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            >
              <TrendingUp className="mr-2 h-4 w-4" /> Project IRR
            </Button>
            <Button
              variant="outline"
              onClick={() => setConfirmDelete(true)}
              data-testid="deal-delete-button"
              className="border-red-200 text-red-700 hover:bg-red-50"
            >
              <Trash2 className="mr-2 h-4 w-4" /> Delete Deal
            </Button>
          </div>
        </div>

        {/* Phase 7 — Cross-currency banner */}
        <FxRiskBanner currencyView={deal.currency_view} />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full overflow-x-auto" data-testid="deal-tabs">
            <TabsTrigger value="summary" data-testid="tab-summary">{t("dealDetail.tabs.summary")}</TabsTrigger>
            {deal?.is_owned ? (
              <TabsTrigger value="performance" data-testid="tab-performance">
                Performance
              </TabsTrigger>
            ) : null}
            <TabsTrigger value="financials" data-testid="tab-financials">{t("dealDetail.tabs.financials")}</TabsTrigger>
            <TabsTrigger value="scenarios" data-testid="tab-scenarios">Scenarios</TabsTrigger>
            <TabsTrigger value="ai" data-testid="tab-ai">{t("dealDetail.tabs.ai")}</TabsTrigger>
            <TabsTrigger value="risk" data-testid="tab-risk">{t("dealDetail.tabs.risk")}</TabsTrigger>
            <TabsTrigger value="comps" data-testid="tab-comps">Comps</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
            <TabsTrigger value="projection" data-testid="tab-projection">Projection</TabsTrigger>
            <TabsTrigger value="negotiation" data-testid="tab-negotiation">{t("dealDetail.tabs.negotiation")}</TabsTrigger>
            <TabsTrigger value="criteria" data-testid="tab-criteria">Criteria</TabsTrigger>
            <TabsTrigger value="syndication" data-testid="tab-syndication">Syndication</TabsTrigger>
            <TabsTrigger value="report" data-testid="tab-report">{t("dealDetail.tabs.report")}</TabsTrigger>
          </TabsList>

          {/* Summary */}
          <TabsContent value="summary" className="mt-6 space-y-6">
            <TabErrorBoundary tabName="Summary">
            <MarketSnapshotCard deal={deal} onRefresh={(d) => setDeal(d)} />
            <AiHeroPanel deal={deal} onRun={() => runAi("analyze")} running={aiRunning.analyze} />

            {/* Phase 36 Batch AH — Tri-View Deal Score. Sits directly
                under the AI Verdict so the eye reads:
                  1. The AI's headline verdict (active basis)
                  2. How the score shifts across Asking / Counter / Target.
                Clicking Asking or Counter PATCHes the deal basis and the
                rest of the Summary tab + Financials + AI Verdict
                instantly re-renders against the new anchor. Target yield
                is informational only (walk-away anchor).  */}
            {/* Phase 48 — Personal Residence branch. Swap the buyer-
                side Tri-View (Asking / Counter / Target) for the
                owner-occupant Tri-View (Monthly Cost / Break-Even /
                5-Year Equity), then render the Alternative Scenarios
                hero card directly below it. Rental / flip / BRRRR
                still get the standard DealScoreTriView. */}
            {deal.deal_type === "personal_residence" ? (
              <>
                <PersonalResidenceTriView deal={deal} />
                <PersonalResidenceScenarios scenarios={deal.scenarios_comparison} />
              </>
            ) : (
              <DealScoreTriView deal={deal} onDealUpdated={setDeal} />
            )}

            {/* Phase 44 Batch BF2 — Exit-as-Rental collapsible.
                Mounted directly below the Tri-View on flip deals so
                the analyst can compare the flip thesis to a buy-and-
                hold thesis without clicking into the Financials tab.
                Reads the same component as the Financials tab (single
                source of truth for the rental-exit math). */}
            {deal.deal_type === "flip" ? (
              <ExitAsRentalPanel deal={deal} />
            ) : null}

            {/* Phase 36 Batch AJ — Score Breakdown ("Why 72/100? How to
                move to Buy?"). Sits directly under the tri-view so the
                three gauges are the headline and the breakdown is the
                explainer. Re-fetches on basis flip via its own effect
                hook. */}
            <ScoreBreakdownPanel deal={deal} />

            {/* Phase 36 Batch X — FX-Adjusted Returns panel. Renders ONLY
                for cross-currency deals (property_currency ≠ user's base).
                A USD investor in a US deal sees nothing here — the
                component returns null and we have one less section. */}
            <FXAdjustedReturnsPanel fxView={deal.fx_view} currencyRisk={currencyRisk} />

            {/* Phase 36 Batch Y — Historical FX line chart. Renders
                immediately below the FX-adjusted returns panel and is
                hidden when fx_view.fx_applicable is false (same-currency
                deals never show it). Lazily fetches via /api/deals/{id}/history. */}
            <HistoricalFXChart dealId={id} fxView={deal.fx_view} />

            {/* Batch I — Staleness banner: detects when ai_summary's snapshot
                inputs diverged from current saved deal values (e.g. user
                edited the price after the AI ran). */}
            <StaleAnalysisBanner
              deal={deal}
              onRerun={() => runAi("analyze")}
              running={aiRunning.analyze}
            />

            {/* Batch I → Batch AG — KPI section label is basis-aware.
                After Batch AF, `deal.purchase_price` is the EFFECTIVE
                price (counter-offer / AI-rec / original), so the
                amount is always right; we just need the suffix to
                stop misleadingly saying "asking" when the user has
                applied a counter-offer. */}
            <div className="flex items-baseline justify-between gap-2 border-b border-slate-200 pb-2">
              <h3 data-testid="kpi-section-header" className="text-base font-semibold text-slate-900">
                Key metrics
                <span className="ml-2 text-sm font-normal text-slate-500">
                  at {fmt.money(deal.purchase_price, { primaryOnly: true })} ({effectivePriceBasisSuffix(deal)})
                </span>
              </h3>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {deal.deal_type === "flip" ? (
                <>
                  <KpiTile
                    label="Gross Profit"
                    value={fmt.money(deal.gross_profit, { cents: true })}
                    tone={(deal.gross_profit || 0) >= 0 ? "good" : "bad"}
                    testid="kpi-gross-profit"
                    trace={deal.trace} traceMetric="gross_profit" traceEnabled={traceEnabled}
                  />
                  <KpiTile
                    label="Annualised ROI"
                    value={deal.annualized_roi_pct != null ? `${Number(deal.annualized_roi_pct).toFixed(2)}%` : "—"}
                    testid="kpi-roi-annualized"
                    trace={deal.trace} traceMetric="annualized_roi_pct" traceEnabled={traceEnabled}
                  />
                  <KpiTile
                    label="ARV Ratio"
                    value={deal.deal_to_arv_pct != null ? `${Number(deal.deal_to_arv_pct).toFixed(1)}%` : "—"}
                    tone={deal.deal_to_arv_pct != null && deal.deal_to_arv_pct <= 70 ? "good" : "bad"}
                    testid="kpi-arv-ratio"
                    trace={deal.trace} traceMetric="arv_ratio_70_rule" traceEnabled={traceEnabled}
                  />
                  <KpiTile
                    label="ARV"
                    value={fmt.money(deal.arv)}
                    testid="kpi-arv"
                  />
                  <KpiTile
                    label="Total Project Cost"
                    value={fmt.money(deal.total_project_cost)}
                    testid="kpi-project-cost"
                    trace={deal.trace} traceMetric="total_project_cost" traceEnabled={traceEnabled}
                  />
                  <KpiTile
                    label="Total Cash Invested"
                    value={fmt.money(deal.total_cash_invested)}
                    testid="kpi-cash-invested"
                  />
                  <KpiTile
                    label="Holding (months)"
                    value={deal.holding_period_months ?? "—"}
                    testid="kpi-hold-months"
                  />
                  <KpiTile
                    label="Breakeven ARV"
                    value={fmt.money(deal.breakeven_arv)}
                    testid="kpi-breakeven-arv"
                    trace={deal.trace} traceMetric="breakeven_arv" traceEnabled={traceEnabled}
                  />
                </>
              ) : (
                <></>
              )}
              {deal.deal_type !== "flip" ? (
                <>
              <KpiTile
                label="Monthly Cash Flow"
                value={fmt.money(deal.cash_flow_monthly, { cents: true })}
                tone={cashFlowTone}
                testid="kpi-cf-monthly"
                trace={deal.trace} traceMetric="cash_flow_monthly" traceEnabled={traceEnabled}
              />
              <KpiTile
                label="Annual Cash Flow"
                value={fmt.money(deal.cash_flow_annual, { cents: true })}
                tone={cashFlowTone}
                testid="kpi-cf-annual"
              />
              <KpiTile
                label="Cap Rate"
                value={fmt.percent(deal.cap_rate)}
                testid="kpi-cap"
                trace={deal.trace} traceMetric="cap_rate" traceEnabled={traceEnabled}
              />
              <KpiTile
                label="Cash-on-Cash"
                value={fmt.percent(deal.cash_on_cash)}
                tone={deal.cash_on_cash >= 0 ? "good" : "bad"}
                testid="kpi-coc"
                trace={deal.trace} traceMetric="cash_on_cash" traceEnabled={traceEnabled}
              />
              <KpiTile
                label="NOI (Monthly)"
                value={fmt.money(deal.noi_monthly, { cents: true })}
                testid="kpi-noi-monthly"
                trace={deal.trace} traceMetric="noi_monthly" traceEnabled={traceEnabled}
              />
              <KpiTile
                label="NOI (Annual)"
                value={fmt.money(deal.noi_annual, { cents: true })}
                testid="kpi-noi-annual"
              />
              <KpiTile
                label="DSCR"
                value={formatDscr(deal.dscr)}
                testid="kpi-dscr"
              />
              <KpiTile
                label="Break-even Occupancy"
                value={fmt.percent(deal.break_even_occupancy)}
                testid="kpi-breakeven"
              />
              <KpiTile
                label="Total Cash Invested"
                value={fmt.money(deal.total_cash_invested)}
                testid="kpi-cash-invested"
              />
              <KpiTile
                label="Loan Amount"
                value={fmt.money(deal.loan_amount)}
                testid="kpi-loan"
              />
              <KpiTile
                label="Down Payment"
                value={fmt.money(deal.down_payment_amount)}
                testid="kpi-down"
              />
              <KpiTile
                label="Monthly Debt Service"
                value={fmt.money(deal.monthly_debt_service, { cents: true })}
                testid="kpi-debt"
              />
                </>
              ) : null}
            </div>

            {/* Batch I — Scenario comparison (asking vs counter-offer side-by-side).
                Self-fetches via /intelligence/deal-at-price; inline counter edit
                PATCHes the canonical counter_offer_price field. */}
            <ScenarioComparison
              deal={deal}
              onCounterSaved={async () => {
                try {
                  const { data } = await api.get(`/deals/${id}`);
                  setDeal(data);
                } catch (err) {
                  console.warn("post-counter deal refetch failed", err);
                }
              }}
            />

            {/* Phase 7 → Batch AG — Tax impact panel label is basis-aware. */}
            <div className="flex items-baseline justify-between gap-2 border-b border-slate-200 pb-2">
              <h3 data-testid="tax-section-header" className="text-base font-semibold text-slate-900">
                Tax impact
                <span className="ml-2 text-sm font-normal text-slate-500">
                  at {fmt.money(deal.purchase_price, { primaryOnly: true })} ({effectivePriceBasisSuffix(deal)})
                </span>
              </h3>
            </div>
            <TaxImpactPanel deal={deal} />

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">Property</h3>
                <dl className="mt-3 grid gap-2 text-sm">
                  <div className="flex justify-between"><dt className="text-slate-500">Address</dt><dd className="font-medium text-slate-900 text-right">{p.address}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Location</dt><dd className="text-slate-900">{[p.city, p.state, p.zip].filter(Boolean).join(", ")}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Type</dt><dd className="text-slate-900">{propertyTypeLabel(p.property_type)}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Beds / Baths</dt><dd className="text-slate-900">{p.bedrooms} / {p.bathrooms}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Square feet</dt><dd className="text-slate-900">{p.square_feet?.toLocaleString?.() ?? p.square_feet}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Asking price</dt><dd className="font-semibold text-slate-900">{fmt.money(p.asking_price)}</dd></div>
                </dl>
              </div>

              <details className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="inputs-snapshot">
                <summary className="cursor-pointer list-none text-sm font-semibold uppercase tracking-wider text-slate-500">
                  Inputs snapshot
                </summary>
                {/* Batch BB — data-quality warning badges. Render before
                    the input list so the user sees the "this field is
                    unreliable" signal directly next to the entry it
                    relates to. Closing-costs is amber (AZ), taxes is
                    amber (BB W1), rent-stress escalates to red when
                    the stress verdict drops 2+ tiers (BB W2). */}
                <div className="mt-4 flex flex-wrap gap-2" data-testid="data-quality-badges">
                  {deal?.closing_costs_warning ? (
                    <span data-testid="badge-closing-costs-warning"
                          className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-900">
                      <span aria-hidden>⚠</span> Closing costs $0 · suggest {fmt.money(deal.closing_costs_suggested)}
                    </span>
                  ) : null}
                  {deal?.taxes_warning ? (
                    <span data-testid="badge-taxes-warning"
                          className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-900">
                      <span aria-hidden>⚠</span> Property tax $0 · typical {fmt.money(deal.taxes_suggested_low)}–{fmt.money(deal.taxes_suggested_high)}/yr
                    </span>
                  ) : null}
                  {deal?.rent_stress_warning ? (
                    <span data-testid="badge-rent-stress-warning"
                          className={
                            deal?.rent_stress_test_failed
                              ? "inline-flex items-center gap-1.5 rounded-full border border-red-300 bg-red-50 px-3 py-1 text-xs font-semibold text-red-900"
                              : "inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-900"
                          }>
                      <span aria-hidden>{deal?.rent_stress_test_failed ? "🚨" : "⚠"}</span>
                      Rent {Math.round((deal?.rent_above_median_pct || 0) * 100)}% above comp median ({fmt.money(deal?.rent_market_median)}/mo)
                    </span>
                  ) : null}
                  {/* Phase 44 Batch BF1 — condo with no assessment disclosure.
                      The data_confidence layer marks the slot as `unverified`
                      automatically for condos/townhouses with an empty array;
                      we surface that here as a soft amber warning. */}
                  {deal?.data_confidence?.fields?.special_assessments_disclosed === "unverified" ? (
                    <span data-testid="badge-special-assessments-warning"
                          className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-900"
                          title="Florida and Surfside-affected condos commonly carry $200–500/mo assessments. Request the estoppel letter to confirm.">
                      <span aria-hidden>⚠</span>
                      Special assessments not verified · request estoppel letter
                    </span>
                  ) : null}
                </div>
                <dl className="mt-4 grid gap-2 text-sm">
                  <div className="flex justify-between"><dt className="text-slate-500">Purchase price</dt><dd className="font-medium text-slate-900">{fmt.money(deal.purchase_price)}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Monthly rent</dt><dd className="font-medium text-slate-900">{fmt.money(deal.monthly_rent)}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Taxes (annual)</dt><dd className="text-slate-900">{fmt.money(deal.taxes_annual)}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Insurance (annual)</dt><dd className="text-slate-900">{fmt.money(deal.insurance_annual)}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">HOA</dt><dd className="text-slate-900">{fmt.money(deal.hoa_monthly)} / mo</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Utilities</dt><dd className="text-slate-900">{fmt.money(deal.utilities_monthly)} / mo</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Vacancy / Management</dt><dd className="text-slate-900">{deal.vacancy_percent}% / {deal.management_percent}%</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Repairs (upfront)</dt><dd className="text-slate-900">{fmt.money(deal.repairs)}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Down payment</dt><dd className="text-slate-900">{deal.down_payment_percent}%</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Interest rate</dt><dd className="text-slate-900">{deal.interest_rate}% · {deal.loan_term_years} yrs</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Closing costs</dt><dd className="text-slate-900">{fmt.money(deal.closing_costs)}</dd></div>
                </dl>
                {/* Phase 44 Batch BF1 — special assessments table. Renders
                    below the input snapshot ONLY when the deal carries
                    at least one assessment. No empty state — keeps the
                    snapshot tight for the 90% of deals that have none. */}
                {Array.isArray(deal.special_assessments) && deal.special_assessments.length > 0 ? (
                  <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50/40 p-4" data-testid="deal-special-assessments">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Special assessments
                    </p>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-slate-500">
                          <th className="pb-2 text-left font-medium">Name</th>
                          <th className="pb-2 text-right font-medium">Monthly</th>
                          <th className="pb-2 text-right font-medium">Remaining</th>
                          <th className="pb-2 text-right font-medium">Balance</th>
                          <th className="pb-2 text-right font-medium">Paid at closing?</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {deal.special_assessments.map((sa, i) => {
                          const balance =
                            sa.outstanding_balance ??
                            (Number(sa.monthly_amount) || 0) * (Number(sa.payments_remaining) || 0);
                          return (
                            <tr key={sa.id || i} className="text-slate-700" data-testid={`deal-special-assessment-${i}`}>
                              <td className="py-2 pr-2 text-slate-900">{sa.name || "(unnamed)"}</td>
                              <td className="py-2 text-right tabular-nums">
                                {fmt.money(sa.monthly_amount, { cents: true })}/mo
                              </td>
                              <td className="py-2 text-right tabular-nums">{sa.payments_remaining} mo</td>
                              <td className="py-2 text-right tabular-nums">{fmt.money(balance)}</td>
                              <td className="py-2 text-right">
                                {sa.pay_off_at_closing ? (
                                  <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-700">
                                    Paid at closing
                                  </span>
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {Number(deal.special_assessments_monthly) > 0 && Number(deal.hoa_monthly) > 0 ? (
                      <p className="mt-3 text-xs text-slate-600">
                        HOA + Assessments:{" "}
                        <span className="font-medium tabular-nums text-slate-900">
                          {fmt.money(deal.hoa_monthly, { cents: true })} +{" "}
                          {fmt.money(deal.special_assessments_monthly, { cents: true })} ={" "}
                          {fmt.money(
                            Number(deal.hoa_monthly) + Number(deal.special_assessments_monthly),
                            { cents: true },
                          )}
                          /mo
                        </span>
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </details>
            </div>
            </TabErrorBoundary>
          </TabsContent>

          {/* Phase 19 — Investment Performance (only when is_owned=true) */}
          {deal?.is_owned ? (
            <TabsContent value="performance" className="mt-6" data-testid="performance-tab-content">
              <TabErrorBoundary tabName="Performance">
                <PerformanceTab deal={deal} />
              </TabErrorBoundary>
            </TabsContent>
          ) : null}

          {/* Financials — conditional by deal_type */}
          <TabsContent value="financials" className="mt-6" data-testid="financials-tab-content">
            <TabErrorBoundary tabName="Financials">
            <AnalysisBasisChip deal={deal} className="mb-4" />
            <EffectivePriceBanner deal={deal} onDealUpdated={setDeal} />
            {deal.deal_type === "flip" ? (
              <FlipFinancialsTab deal={deal} />
            ) : deal.deal_type === "brrrr" ? (
              <BRRRRFinancialsTab deal={deal} />
            ) : (
              <div className="space-y-6">
                {deal.deal_type === "multifamily" ? <MultifamilySummaryCard deal={deal} /> : null}
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="pb-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Line item</th>
                    <th className="pb-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <FinancialsRow label="Income" bold divider />
                  <FinancialsRow label="Monthly rent" value={fmt.money(deal.monthly_income, { cents: true })} indent />
                  <FinancialsRow label="Vacancy loss" value={`− ${fmt.money(deal.vacancy_loss, { cents: true })}`} indent />
                  <FinancialsRow
                    label="Effective income"
                    value={fmt.money(deal.monthly_income - deal.vacancy_loss, { cents: true })}
                    indent
                    bold
                  />

                  <FinancialsRow label="Operating expenses" bold divider />
                  <FinancialsRow label="Taxes (monthly)" value={fmt.money(deal.taxes_annual / 12, { cents: true })} indent />
                  <FinancialsRow label="Insurance (monthly)" value={fmt.money(deal.insurance_annual / 12, { cents: true })} indent />
                  <FinancialsRow label="HOA" value={fmt.money(deal.hoa_monthly, { cents: true })} indent />
                  {/* Phase 44 Batch BF1 — surface active special-assessment
                      monthly expense on its own row. Hidden when zero. */}
                  {Number(deal.special_assessments_monthly) > 0 ? (
                    <FinancialsRow
                      label="Special assessments"
                      value={fmt.money(deal.special_assessments_monthly, { cents: true })}
                      indent
                    />
                  ) : null}
                  <FinancialsRow label="Utilities" value={fmt.money(deal.utilities_monthly, { cents: true })} indent />
                  <FinancialsRow label="Management fee" value={fmt.money(deal.management_fee, { cents: true })} indent />
                  <FinancialsRow label="Total monthly expenses" value={fmt.money(deal.monthly_expenses, { cents: true })} indent bold />

                  <FinancialsRow label="NOI (monthly)" value={fmt.money(deal.noi_monthly, { cents: true })} bold divider />
                  <FinancialsRow label="NOI (annual)" value={fmt.money(deal.noi_annual, { cents: true })} indent />

                  <FinancialsRow label="Debt service" bold divider />
                  <FinancialsRow label="Monthly debt service" value={fmt.money(deal.monthly_debt_service, { cents: true })} indent />
                  <FinancialsRow label="Annual debt service" value={fmt.money(deal.annual_debt_service, { cents: true })} indent />

                  <FinancialsRow
                    label="Cash flow (monthly)"
                    value={fmt.money(deal.cash_flow_monthly, { cents: true })}
                    bold
                    divider
                    negative={deal.cash_flow_monthly < 0}
                  />
                  <FinancialsRow
                    label="Cash flow (annual)"
                    value={fmt.money(deal.cash_flow_annual, { cents: true })}
                    indent
                    negative={deal.cash_flow_annual < 0}
                  />

                  <FinancialsRow label="Capitalization" bold divider />
                  <FinancialsRow label="Loan amount" value={fmt.money(deal.loan_amount)} indent />
                  <FinancialsRow label="Down payment" value={fmt.money(deal.down_payment_amount)} indent />
                  <FinancialsRow label="Closing costs" value={fmt.money(deal.closing_costs)} indent />
                  <FinancialsRow label="Repairs" value={fmt.money(deal.repairs)} indent />
                  <FinancialsRow label="Total cash invested" value={fmt.money(deal.total_cash_invested)} indent bold />
                </tbody>
              </table>
            </div>
              </div>
            )}
            </TabErrorBoundary>
          </TabsContent>

          {/* Phase 15 — Scenarios (counter-offer + reverse-yield calculator) */}
          <TabsContent value="scenarios" className="mt-6" data-testid="scenarios-tab-content">
            <TabErrorBoundary tabName="Scenarios">
              <ScenariosTab
                deal={deal}
                onDealUpdated={async () => {
                  try {
                    const { data } = await api.get(`/deals/${deal.id}`);
                    setDeal(data);
                  } catch { /* refresh is best-effort */ }
                }}
              />
            </TabErrorBoundary>
          </TabsContent>

          <TabsContent value="ai" className="mt-6 space-y-6">
            <TabErrorBoundary tabName="Document Health">
              <AnalysisBasisChip deal={deal} />
              <DocumentHealthCard
                dealId={deal?.id}
                onJumpToDocs={() =>
                  document
                    .querySelector('[data-testid="tab-documents"]')
                    ?.click()
                }
              />
            </TabErrorBoundary>
            <TabErrorBoundary tabName="AI Analysis">
              <AiAnalysisTab deal={deal} onRun={() => runAi("analyze")} running={aiRunning.analyze} />
            </TabErrorBoundary>
          </TabsContent>
          <TabsContent value="risk" className="mt-6">
            <TabErrorBoundary tabName="Risk">
              <AnalysisBasisChip deal={deal} className="mb-4" />
              <RiskTab deal={deal} onRun={() => runAi("risks")} running={aiRunning.risks} />
            </TabErrorBoundary>
          </TabsContent>
          <TabsContent value="comps" className="mt-6" data-testid="comps-tab-content">
            <TabErrorBoundary tabName="Comps">
              <CompsTab dealId={deal.id} />
            </TabErrorBoundary>
          </TabsContent>
          <TabsContent value="documents" className="mt-6" data-testid="documents-tab-content">
            <TabErrorBoundary tabName="Documents">
              <DocumentsTab deal={deal} />
            </TabErrorBoundary>
          </TabsContent>
          <TabsContent value="projection" className="mt-6" data-testid="projection-tab-content">
            <TabErrorBoundary tabName="Projection">
              <ProjectionTab dealId={deal?.id} />
            </TabErrorBoundary>
          </TabsContent>
          <TabsContent value="negotiation" className="mt-6">
            <TabErrorBoundary tabName="Negotiation">
              <AnalysisBasisChip deal={deal} className="mb-4" />
              <NegotiationTab
                deal={deal}
                onRun={() => runAi("neg")}
                running={aiRunning.neg}
                onSwitchTab={setActiveTab}
              />
            </TabErrorBoundary>
          </TabsContent>
          <TabsContent value="criteria" className="mt-6" data-testid="criteria-tab-content">
            <TabErrorBoundary tabName="Criteria">
              <CriteriaTab dealId={id} />
            </TabErrorBoundary>
          </TabsContent>
          <TabsContent value="syndication" className="mt-6">
            <TabErrorBoundary tabName="Syndication">
              <SyndicationTab deal={deal} />
            </TabErrorBoundary>
          </TabsContent>
          <TabsContent value="report" className="mt-6">
            <TabErrorBoundary tabName="Report">
              <Reports dealId={id} compact />
            </TabErrorBoundary>
          </TabsContent>
        </Tabs>
      </div>

      <ProjectionModal open={projOpen} onOpenChange={setProjOpen} dealId={id} />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this deal?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the deal and its property from your workspace. This action can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              disabled={deleting}
              data-testid="confirm-delete-action"
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CompareStrategiesModal
        open={compareOpen}
        onOpenChange={setCompareOpen}
        deal={deal}
      />
      <CompareToModal
        open={compareToOpen}
        onOpenChange={setCompareToOpen}
        currentDealId={id}
      />
      {/* Phase 31 — Mark as bought modal + risky-deal confirm gate */}
      <AlertDialog open={riskConfirmOpen} onOpenChange={setRiskConfirmOpen}>
        <AlertDialogContent data-testid="mark-bought-risk-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>This deal scored Risky — are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              Our analysis flagged elevated risk on this property. You can still mark it as
              bought to track actuals, but make sure you have read the risk breakdown first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mark-bought-risk-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setRiskConfirmOpen(false);
                setMarkBoughtOpen(true);
              }}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="mark-bought-risk-continue"
            >
              I understand, continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <MarkAsBoughtModal
        open={markBoughtOpen}
        onOpenChange={setMarkBoughtOpen}
        deal={deal}
        onMarked={(updated) => setDeal(updated)}
        onShareMilestone={() => setShareCardOpen(true)}
      />
      {/* Phase 32 — Repurchase Story Share Card */}
      <ShareCardModal
        open={shareCardOpen}
        onOpenChange={setShareCardOpen}
        deal={deal}
      />
    </AppShell>
  );
}
